'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  CONFIRMATION_DIALOG_CLOSE_REASON,
  ConfirmationDialog,
  type ConfirmationDialogCloseReason,
} from '../../confirmation-dialog/ConfirmationDialog';
import { Checkbox } from '../../../components/checkbox';

export type ChatBranchConfirmChoice = {
  revertFiles: boolean;
  rememberChoice: boolean;
};

export type ChatBranchConfirmVariant = 'composer' | 'ask-user' | 'retry-send';

type ChatBranchConfirmationDialogProps = {
  isOpen: boolean;
  isLoading?: boolean;
  variant?: ChatBranchConfirmVariant;
  onConfirm: (choice: ChatBranchConfirmChoice) => void | Promise<void>;
  onCancel: () => void;
  onOpenChange: (details: { open: boolean }) => void;
};

export function ChatBranchConfirmationDialog({
  isOpen,
  isLoading,
  variant = 'composer',
  onConfirm,
  onCancel,
  onOpenChange,
}: ChatBranchConfirmationDialogProps) {
  const tBranching = useTranslations('designSystem.chat.branching');
  const tChat = useTranslations('designSystem.chat.chat');
  const isRetryingSend = variant === 'retry-send';
  const [rememberChoice, setRememberChoice] = useState(false);
  const isChoiceSubmittedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setRememberChoice(false);
    }
  }, [isOpen]);

  const handleOpenChange = ({ open }: { open: boolean }) => {
    if (!open && isChoiceSubmittedRef.current) {
      isChoiceSubmittedRef.current = false;

      return;
    }

    onOpenChange({ open });
  };

  const handleClose = async (reason: ConfirmationDialogCloseReason) => {
    const isCancelled =
      reason === CONFIRMATION_DIALOG_CLOSE_REASON.Dismiss ||
      (isRetryingSend && reason === CONFIRMATION_DIALOG_CLOSE_REASON.Cancel);

    if (isCancelled) {
      onCancel();

      return;
    }

    isChoiceSubmittedRef.current = true;
    await onConfirm({
      revertFiles: reason === CONFIRMATION_DIALOG_CLOSE_REASON.Confirm,
      rememberChoice,
    });
  };

  const resolveDescription = () => {
    if (isRetryingSend) {
      return tBranching('confirmFailedDescription');
    }

    return variant === 'ask-user'
      ? tBranching('submitFromPreviousDescriptionAskUser')
      : tBranching('submitFromPreviousDescription');
  };

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      onClose={handleClose}
      title={
        isRetryingSend
          ? tBranching('confirmFailedTitle')
          : tBranching('submitFromPreviousTitle')
      }
      description={resolveDescription()}
      confirmText={isRetryingSend ? tChat('retry') : tBranching('revertAction')}
      cancelText={isRetryingSend ? undefined : tBranching('keepAction')}
      loading={isLoading}
      showIcon={false}
      variant="default"
    >
      {isRetryingSend ? null : (
        <Checkbox
          size="sm"
          checked={rememberChoice}
          disabled={isLoading}
          onCheckedChange={({ checked }) => setRememberChoice(checked === true)}
        >
          {tBranching('dontAskAgain')}
        </Checkbox>
      )}
    </ConfirmationDialog>
  );
}
